import { ReactNode, useEffect, useId, useRef } from 'react';
import { IoMdClose } from 'react-icons/io';
import { FiAlertTriangle } from 'react-icons/fi';

interface Props {
    isOpen: boolean;
    title: string;
    icon?: ReactNode;
    headerActions?: ReactNode;
    loading?: boolean;
    error?: string | null;
    onRetry?: () => void;
    onClose: () => void;
    children: ReactNode;
}

/**
 * Right-side detail drawer for b3-custody list screens.
 *
 * Follows the collateral OperationLogsDrawer pattern (overlay + fixed right panel
 * + sticky header + loading state). The close (X) button is an overlay pinned to the
 * left edge of the panel; header actions (Salvar/Editar) live in the sticky header.
 *
 * Accessibility: rendered as a labelled modal dialog (role="dialog" + aria-modal),
 * focuses the close button on open and closes on Escape.
 */
const SidePanelDrawer = ({ isOpen, title, icon, headerActions, loading = false, error = null, onRetry, onClose, children }: Props) => {
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const titleId = useId();

    // Keep the latest onClose without making it an effect dependency — otherwise a new
    // inline onClose on every parent render would re-run the effect and steal focus back
    // to the close button on each keystroke (cursor jumping out of inputs).
    const onCloseRef = useRef(onClose);
    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    useEffect(() => {
        if (!isOpen) return;
        // Focus the close button only when the panel opens (keyboard/assistive tech).
        closeButtonRef.current?.focus();

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onCloseRef.current();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />

            <button
                ref={closeButtonRef}
                type="button"
                onClick={onClose}
                aria-label="Fechar painel"
                className="absolute top-4 right-[min(36rem,100%)] translate-x-1/2 z-[60] flex h-9 w-9 items-center justify-center rounded-full bg-gray-700 text-white shadow-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
                <IoMdClose size={18} />
            </button>

            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="relative w-full max-w-xl bg-white dark:bg-slate-800 h-full overflow-auto shadow-xl"
            >
                <div className="sticky top-0 flex items-center justify-between gap-3 p-4 pl-8 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 z-10">
                    <div className="flex min-w-0 items-center gap-2">
                        {icon}
                        <span id={titleId} className="truncate text-sm font-semibold dark:text-white">{title}</span>
                    </div>
                    {headerActions ? (
                        <div className="flex shrink-0 items-center gap-2">{headerActions}</div>
                    ) : null}
                </div>

                <div className="p-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
                        </div>
                    ) : error ? (
                        <div className="flex flex-col items-center gap-3 py-8 text-center">
                            <FiAlertTriangle size={28} className="text-red-500" />
                            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                            {onRetry ? (
                                <button
                                    type="button"
                                    onClick={onRetry}
                                    className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
                                >
                                    Tentar novamente
                                </button>
                            ) : null}
                        </div>
                    ) : (
                        children
                    )}
                </div>
            </div>
        </div>
    );
};

export default SidePanelDrawer;
